import { UNTITLED_BOARD_NAME } from "@/shared/domain/boards";
import { type BoardCalendar } from "@/shared/domain/boards/boardCalendar";
import {
  ConfirmationAlert,
  ConfirmationBullets,
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationLead,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";

import { BoardCalendarField } from "./BoardCalendarField";
import {
  calendarOfDefinitionMember,
  declarationIdOf,
  definitionNames,
  describeCalendar,
  describePanelLine,
  describeStage,
  describeTimeRange,
  type DefinitionNames,
} from "./boardConfirmationText";
import {
  ApprovalNote,
  DryRunVerdict,
  PanelDryRunStatus,
  StageDryRunStatus,
} from "./BoardDryRunStatus";
import {
  definitionFromCreateInput,
  describeDefinitionShapeProblem,
} from "./boardToolSchema";
import type { ConfirmationContentProps } from "./ClientTools";
import { useBoardDryRun, type BoardDryRunState } from "./useBoardDryRun";
import { useControlCustomFields } from "./useControlCustomFields";

/**
 * Read-only preview of the board definition the model wants to save, with
 * each stage's and panel's dry-run status beside it.
 *
 * The tool declares no `validateInput` guard (its `execute` checks the
 * definition's shape, then the authoring service's dry run decides whether
 * it saves), so `params` is whatever the model emitted and every read is
 * defensive: the card shows what it can read of a malformed proposal and
 * says that the tool will refuse it. The dry run starts as the card mounts
 * and never delays Approve; each status fills in when the report lands.
 */
export function CreateBoardConfirmation({ params }: ConfirmationContentProps) {
  const definition = definitionFromCreateInput(params);
  const shapeProblem = describeDefinitionShapeProblem(definition);
  const dryRun = useBoardDryRun(shapeProblem === null ? definition : undefined);
  const title =
    typeof definition.title === "string" ? definition.title : undefined;
  const panels = Array.isArray(definition.panels)
    ? definition.panels
    : undefined;
  const customFieldsOf = useControlCustomFields(definition);
  const names = definitionNames(customFieldsOf, definition);
  const calendar = calendarOfDefinitionMember(definition.calendar);

  return (
    <ConfirmationPanel label="Create board preview">
      <ConfirmationLead>Save this as a new board?</ConfirmationLead>
      <ShapeProblem shown={shapeProblem !== null} />
      <DryRunVerdict
        dryRun={dryRun}
        shownOnCard={[describeCalendar(definition.calendar)?.problem]}
      />
      <ConfirmationCard title={title ?? UNTITLED_BOARD_NAME}>
        <ConfirmationFields>
          <TimeRangeField
            timeRange={definition.time_range}
            calendar={calendar}
          />
          <BoardCalendarField
            calendar={definition.calendar}
            label="Time zone and week start"
          />
          <StagesField
            stages={definition.stages}
            names={names}
            timeZone={calendar.timeZone}
            dryRun={dryRun}
          />
          <PanelsField panels={panels} names={names} dryRun={dryRun} />
        </ConfirmationFields>
      </ConfirmationCard>
      <ApprovalNote dryRun={dryRun} definitionKnown={shapeProblem === null}>
        Approving first loads every stage and panel once, to check that each
        loads without an error. The board is saved and opened only if all of
        them do; otherwise nothing is saved and the chat is told what failed.
      </ApprovalNote>
    </ConfirmationPanel>
  );
}

/** Says the tool will refuse a proposal whose shape it cannot read; the
 *  reason is written for the chat, which receives it on approval. */
function ShapeProblem({ shown }: { shown: boolean }) {
  if (!shown) {
    return null;
  }
  return (
    <ConfirmationAlert>
      This request won&rsquo;t succeed: the board the chat sent can&rsquo;t be
      read. Approving saves nothing and tells the chat why.
    </ConfirmationAlert>
  );
}

function TimeRangeField({
  timeRange,
  calendar,
}: {
  timeRange: unknown;
  calendar: BoardCalendar;
}) {
  const label = describeTimeRange(timeRange, calendar);
  if (label === undefined) {
    return null;
  }
  return <ConfirmationField label="Time range">{label}</ConfirmationField>;
}

/** The stages the board declares, one line each with its dry-run status
 *  beside it, a control's values written in `timeZone`; nothing when it
 *  declares none. */
function StagesField({
  stages,
  names,
  timeZone,
  dryRun,
}: {
  stages: unknown;
  names: DefinitionNames;
  timeZone: string;
  dryRun: BoardDryRunState;
}) {
  if (!Array.isArray(stages) || stages.length === 0) {
    return null;
  }
  return (
    <ConfirmationField label={`Stages (${stages.length})`}>
      <ConfirmationBullets>
        {stages.map((stage, index) => (
          <li key={index}>
            {describeStage(stage, names, timeZone)}
            <StageDryRunStatus
              dryRun={dryRun}
              stageId={declarationIdOf(stage)}
              stageIndex={index}
            />
          </li>
        ))}
      </ConfirmationBullets>
    </ConfirmationField>
  );
}

function PanelsField({
  panels,
  names,
  dryRun,
}: {
  panels: unknown[] | undefined;
  names: DefinitionNames;
  dryRun: BoardDryRunState;
}) {
  if (panels === undefined) {
    return <ConfirmationField label="Panels">(missing)</ConfirmationField>;
  }
  if (panels.length === 0) {
    return <ConfirmationField label="Panels">(none)</ConfirmationField>;
  }
  return (
    <ConfirmationField label={`Panels (${panels.length})`}>
      <ConfirmationBullets>
        {panels.map((panel, index) => (
          <li key={index}>
            {describePanelLine(panel, names)}
            <PanelDryRunStatus
              dryRun={dryRun}
              panelId={declarationIdOf(panel)}
              panelIndex={index}
            />
          </li>
        ))}
      </ConfirmationBullets>
    </ConfirmationField>
  );
}
